import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AdminLayout } from '@/components/AdminLayout';
import adminApi from '@/lib/adminApi';
import { formatApiError } from '@/lib/AuthContext';
import { Building2, Ban, CheckCircle, Eye, Clock, CreditCard, Gift, XCircle, CalendarPlus, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

const TABS = ['overview', 'users', 'billing', 'audit'];

const BILLING_STATUS_BADGE = {
  active: 'bg-green-100 text-green-700',
  trialing: 'bg-blue-100 text-blue-700',
  past_due: 'bg-amber-100 text-amber-700',
  canceled: 'bg-red-100 text-red-700',
  unpaid: 'bg-red-100 text-red-700',
  expired: 'bg-gray-100 text-gray-500',
};

function fmtDate(iso) {
  if (!iso) return 'N/A';
  return new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
}

function planLabel(sub) {
  if (!sub || !sub.plan) return 'No subscription';
  return sub.plan === 'monthly' ? 'Monthly ($2,500/mo)' : 'Annual ($25,000/yr)';
}

function isComplimentary(sub) {
  return sub && sub.status === 'active' && !sub.stripe_subscription_id;
}

export default function AdminOrgDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [org, setOrg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview');
  const [actionLoading, setActionLoading] = useState('');

  const fetchOrg = useCallback(async () => {
    try {
      const { data } = await adminApi.getOrganisation(id);
      setOrg(data);
    } catch (error) {
      console.error('Failed to load organisation:', error);
    }
    setLoading(false);
  }, [id]);

  useEffect(() => { fetchOrg(); }, [fetchOrg]);

  const handleAction = async (action, payload = {}) => {
    setActionLoading(action);
    try {
      await adminApi.updateOrganisation(id, payload);
      await fetchOrg();
    } catch (err) {
      alert(formatApiError(err.response?.data?.detail));
    }
    setActionLoading('');
  };

  const handleImpersonate = async () => {
    const reason = prompt('Enter reason for impersonation:');
    if (!reason) return;
    try {
      await adminApi.startImpersonation(id, reason);
      navigate('/dashboard');
    } catch (err) {
      alert(formatApiError(err.response?.data?.detail));
    }
  };

  const handleGrantAccess = async () => {
    const plan = prompt('Grant complimentary access.\n\nPlan (monthly or annual):');
    if (!plan || !['monthly', 'annual'].includes(plan)) { alert('Enter "monthly" or "annual"'); return; }
    const daysStr = prompt(`Duration in days (default: ${plan === 'monthly' ? 30 : 365}):`);
    const days = daysStr ? parseInt(daysStr, 10) : null;
    if (!window.confirm(`Grant complimentary ${plan} access${days ? ` for ${days} days` : ''}?`)) return;
    setActionLoading('grant');
    try {
      const { data } = await adminApi.grantAccess(id, plan, days);
      toast.success(data.message);
      await fetchOrg();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    setActionLoading('');
  };

  const handleRevokeAccess = async () => {
    if (!window.confirm('Revoke this organisation\'s access? They will lose platform access immediately.')) return;
    setActionLoading('revoke');
    try {
      const { data } = await adminApi.revokeAccess(id);
      toast.success(data.message);
      await fetchOrg();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    setActionLoading('');
  };

  const handleExtendPeriod = async () => {
    const daysStr = prompt('Extend subscription period by how many days?');
    if (!daysStr) return;
    const days = parseInt(daysStr, 10);
    if (isNaN(days) || days <= 0) { alert('Enter a positive number'); return; }
    if (!window.confirm(`Extend period by ${days} days?`)) return;
    setActionLoading('extend');
    try {
      const { data } = await adminApi.extendPeriod(id, days);
      toast.success(data.message);
      await fetchOrg();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    setActionLoading('');
  };

  if (loading) return <AdminLayout title="Loading..."><div /></AdminLayout>;
  if (!org) return <AdminLayout title="Not found"><p className="text-[#4E6974]">Organisation not found</p></AdminLayout>;

  const sub = org.subscription;
  const statusBadge = (s) => {
    const c = { active: 'bg-green-100 text-green-700', suspended: 'bg-red-100 text-red-700' };
    return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${c[s] || 'bg-gray-100 text-gray-600'}`}>{s}</span>;
  };

  const billingStatusBadge = () => {
    const st = sub?.status || 'none';
    const comp = isComplimentary(sub);
    const color = BILLING_STATUS_BADGE[st] || 'bg-gray-100 text-gray-500';
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${color}`}>
        {comp && <Gift className="h-3 w-3" />}
        {st === 'active' && comp ? 'Active (complimentary)' : st === 'none' || !st ? 'No subscription' : st}
      </span>
    );
  };

  return (
    <AdminLayout breadcrumbs={[{ label: 'Organisations', href: '/admin/organisations' }, { label: org.name }]}>
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#0A2533] flex items-center gap-3" data-testid="admin-org-name">
            <Building2 className="h-6 w-6 text-[#0097A7]" /> {org.name} {statusBadge(org.status)}
          </h1>
          <p className="text-sm text-[#4E6974] mt-1 font-mono">{org.slug} &middot; {org.user_count} users</p>
        </div>
        <div className="flex items-center gap-2">
          {org.status === 'active' ? (
            <button data-testid="admin-org-suspend-btn" onClick={() => handleAction('suspend', { status: 'suspended' })} disabled={!!actionLoading}
              className="flex items-center gap-1.5 px-3 py-1.5 border border-red-300 text-red-600 text-sm rounded-lg hover:bg-red-50">
              <Ban className="h-3.5 w-3.5" /> Suspend
            </button>
          ) : (
            <button data-testid="admin-org-activate-btn" onClick={() => handleAction('activate', { status: 'active' })} disabled={!!actionLoading}
              className="flex items-center gap-1.5 px-3 py-1.5 border border-green-300 text-green-600 text-sm rounded-lg hover:bg-green-50">
              <CheckCircle className="h-3.5 w-3.5" /> Activate
            </button>
          )}
          <button data-testid="admin-org-impersonate-btn" onClick={handleImpersonate}
            className="flex items-center gap-1.5 px-3 py-1.5 border border-[#E0E0E0] text-[#4E6974] text-sm rounded-lg hover:bg-[#F5F5F5]">
            <Eye className="h-3.5 w-3.5" /> Impersonate owner
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-0 border-b border-[#E8ECF0] mb-6">
        {TABS.map(t => (
          <button key={t} onClick={() => setTab(t)}
            className={`px-4 py-2.5 text-sm capitalize transition-colors ${tab === t ? 'text-[#0097A7] border-b-2 border-[#0097A7] font-medium' : 'text-[#4E6974] hover:text-[#0A2533]'}`}
            data-testid={`admin-org-tab-${t}`}>{t}</button>
        ))}
      </div>

      {/* Overview tab */}
      {tab === 'overview' && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            {[['Created', fmtDate(org.created_at)], ['Last activity', fmtDate(org.last_activity)],
              ['Max users', org.max_users], ['Status', org.status], ['Users', org.user_count]].map(([l, v]) => (
              <div key={l} className="bg-white rounded-lg border border-[#E8ECF0] p-4">
                <p className="text-xs text-[#4E6974] mb-1">{l}</p>
                <p className="text-sm font-medium text-[#0A2533] capitalize">{v}</p>
              </div>
            ))}
          </div>
          {/* Billing summary row */}
          <div className="bg-white rounded-lg border border-[#E8ECF0] p-4" data-testid="admin-org-billing-summary">
            <div className="flex items-center gap-2 mb-3">
              <CreditCard className="h-4 w-4 text-[#0097A7]" />
              <h3 className="text-sm font-semibold text-[#0A2533]">Billing</h3>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-xs text-[#4E6974] mb-1">Plan</p>
                <p className="text-sm font-medium text-[#0A2533]">{planLabel(sub)}</p>
              </div>
              <div>
                <p className="text-xs text-[#4E6974] mb-1">Billing status</p>
                {billingStatusBadge()}
              </div>
              <div>
                <p className="text-xs text-[#4E6974] mb-1">Current period</p>
                <p className="text-sm text-[#0A2533]">
                  {sub?.current_period_start && sub?.current_period_end
                    ? `${fmtDate(sub.current_period_start)} - ${fmtDate(sub.current_period_end)}`
                    : 'N/A'}
                </p>
              </div>
              <div>
                <p className="text-xs text-[#4E6974] mb-1">Auto-renew</p>
                <p className="text-sm text-[#0A2533]">
                  {!sub || sub.status === 'none' ? 'N/A' : sub.cancel_at_period_end ? 'Cancels at period end' : 'Yes'}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Users tab */}
      {tab === 'users' && (
        <div className="bg-white rounded-xl border border-[#E8ECF0] overflow-hidden" data-testid="admin-org-users-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#E8ECF0] bg-[#F8FAFB]">
                <th className="text-left px-4 py-3 text-xs font-semibold text-[#4E6974] uppercase">Name</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-[#4E6974] uppercase">Email</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-[#4E6974] uppercase">Role</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-[#4E6974] uppercase">Status</th>
              </tr>
            </thead>
            <tbody>
              {(org.users || []).map(u => (
                <tr key={u.id} className="border-b border-[#F5F5F5] hover:bg-[#FAFAFA] cursor-pointer" onClick={() => navigate(`/admin/users/${u.id}`)}>
                  <td className="px-4 py-3 font-medium text-[#0A2533]">{u.name}</td>
                  <td className="px-4 py-3 text-[#4E6974]">{u.email}</td>
                  <td className="px-4 py-3 text-[#4E6974] capitalize">{u.role}</td>
                  <td className="px-4 py-3">{statusBadge(u.status)}</td>
                </tr>
              ))}
              {(!org.users || org.users.length === 0) && <tr><td colSpan={4} className="text-center py-6 text-[#B0BEC5]">No users</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {/* Billing tab */}
      {tab === 'billing' && (
        <div className="space-y-6" data-testid="admin-org-billing-tab">
          {/* Stripe references */}
          <div className="bg-white rounded-xl border border-[#E8ECF0] p-5">
            <h3 className="text-sm font-semibold text-[#0A2533] mb-3">Stripe details</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-xs text-[#4E6974] mb-1">Stripe Customer ID</p>
                {sub?.stripe_customer_id ? (
                  <a href={`https://dashboard.stripe.com/customers/${sub.stripe_customer_id}`} target="_blank" rel="noopener noreferrer"
                    className="text-[#0097A7] hover:underline flex items-center gap-1 text-sm">{sub.stripe_customer_id} <ExternalLink className="h-3 w-3" /></a>
                ) : <p className="text-[#B0BEC5]">N/A</p>}
              </div>
              <div>
                <p className="text-xs text-[#4E6974] mb-1">Stripe Subscription ID</p>
                {sub?.stripe_subscription_id ? (
                  <a href={`https://dashboard.stripe.com/subscriptions/${sub.stripe_subscription_id}`} target="_blank" rel="noopener noreferrer"
                    className="text-[#0097A7] hover:underline flex items-center gap-1 text-sm">{sub.stripe_subscription_id} <ExternalLink className="h-3 w-3" /></a>
                ) : <p className="text-[#B0BEC5]">N/A {isComplimentary(sub) && '(complimentary)'}</p>}
              </div>
              <div>
                <p className="text-xs text-[#4E6974] mb-1">Payment method</p>
                <p className="text-sm text-[#0A2533]">{sub?.payment_method_type || 'N/A'}</p>
              </div>
              <div>
                <p className="text-xs text-[#4E6974] mb-1">Latest invoice</p>
                <p className="text-sm text-[#0A2533]">{sub?.latest_invoice_id || 'N/A'}</p>
              </div>
            </div>
          </div>

          {/* Admin override actions */}
          <div className="bg-white rounded-xl border border-[#E8ECF0] p-5">
            <h3 className="text-sm font-semibold text-[#0A2533] mb-4">Admin actions</h3>
            <div className="flex flex-wrap gap-3">
              <button onClick={handleGrantAccess} disabled={!!actionLoading} data-testid="admin-org-grant-access-btn"
                className="flex items-center gap-1.5 px-4 py-2 bg-[#00707C] hover:bg-[#005F6A] disabled:bg-[#B0BEC5] text-white text-sm rounded-lg transition-colors">
                <Gift className="h-3.5 w-3.5" /> Grant access
              </button>
              {sub && sub.status === 'active' && (
                <>
                  <button onClick={handleExtendPeriod} disabled={!!actionLoading} data-testid="admin-org-extend-btn"
                    className="flex items-center gap-1.5 px-4 py-2 border border-[#0097A7] text-[#0097A7] text-sm rounded-lg hover:bg-[#0097A7]/5 transition-colors">
                    <CalendarPlus className="h-3.5 w-3.5" /> Extend period
                  </button>
                  <button onClick={handleRevokeAccess} disabled={!!actionLoading} data-testid="admin-org-revoke-btn"
                    className="flex items-center gap-1.5 px-4 py-2 border border-red-300 text-red-600 text-sm rounded-lg hover:bg-red-50 transition-colors">
                    <XCircle className="h-3.5 w-3.5" /> Revoke access
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Billing notes */}
          <div className="bg-white rounded-xl border border-[#E8ECF0] p-5">
            <label className="text-xs text-[#4E6974] block mb-1">Billing notes</label>
            <textarea value={org.billing_notes || ''} className="w-full border border-[#E0E0E0] rounded-lg px-3 py-2 text-sm min-h-[80px]" readOnly />
          </div>
        </div>
      )}

      {/* Audit tab */}
      {tab === 'audit' && (
        <div className="bg-white rounded-xl border border-[#E8ECF0] p-5 space-y-2" data-testid="admin-org-audit">
          {(org.audit_log || []).length === 0 && <p className="text-sm text-[#B0BEC5]">No audit entries</p>}
          {(org.audit_log || []).map(entry => (
            <div key={entry.id} className="flex items-center gap-3 py-2 border-b border-[#F5F5F5] last:border-0">
              <Clock className="h-3.5 w-3.5 text-[#B0BEC5]" />
              <span className="text-sm text-[#0A2533] flex-1">{entry.action}</span>
              {entry.metadata && Object.keys(entry.metadata).length > 0 && (
                <span className="text-xs text-[#B0BEC5] max-w-[200px] truncate">{JSON.stringify(entry.metadata)}</span>
              )}
              <span className="text-xs text-[#B0BEC5]">{new Date(entry.created_at).toLocaleString()}</span>
            </div>
          ))}
        </div>
      )}
    </AdminLayout>
  );
}
